import React, { useState, useCallback, useEffect } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { Card, Toast, Form, Col, Row } from "react-bootstrap";
import { Formik } from "formik";
import {
  updateTemplate,
  getTemplateDetails,
} from "../../modules/Template/templateSlice";

import { updateTemplateSchema } from "../../schema/validationSchemas";
import Editor from "../../components/commonEditor";

export default function EditTemplate() {
  const { id: templateId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const [template, setTemplate] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templateImages, setTemplateImages] = useState([]);
  const [oldImages, setOldImages] = useState([]);

  const dispatch = useDispatch();

  const handleModalClose = () => {
    setTemplateImages([]);
    setOldImages([]);
    setTemplate(null);
    setIsSubmitting(false);
    setSnackbarMessage("");
    setShowSnackbar(false);
    navigate("/templates");
  };

  const fetchTemplateDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatch(getTemplateDetails(templateId));
      if (res.payload.success) {
        const template = res.payload.data;
        setTemplate(template);
        setOldImages(template.template_images || []);
      } else {
        console.error("Failed to fetch template details", res.payload.message);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching template details:", error);
    } finally {
        setLoading(false);
      }
  }, [dispatch, templateId, setLoading]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = [];

    files.forEach((file) => {
      const exists = templateImages.some(
        (img) => img.name === file.name && img.size === file.size
      );
      if (!exists) {
        newFiles.push(file);
      }
    });

    setTemplateImages((prevImages) => [...prevImages, ...newFiles]);
    event.target.value = null;
  };

  const handleRemoveImage = (index) => {
    setTemplateImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleRemoveOldImage = (imageId) => {
    setOldImages((prevImages) =>
      prevImages.filter((img) => img.id !== imageId)
    );
  };

  useEffect(() => {
    if (templateId) {
      fetchTemplateDetails();
    }
  }, [templateId, fetchTemplateDetails]);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    const updatedValues = {
      ...values,
      id: templateId,
    };
    const formData = new FormData();
    formData.append("_method", "PUT");

    Object.keys(updatedValues).forEach((key) => {
      formData.append(key, updatedValues[key]);
    });

    templateImages.forEach((image, index) => {
      formData.append(`template_images[${index}]`, image);
    });

    const remainingImageIds = oldImages.map((image) => image.id);
    formData.append("previous_images", JSON.stringify(remainingImageIds));

    try {
      const res = await dispatch(updateTemplate(formData));
      if (res.payload && res.payload.success) {
        localStorage.setItem("template_notify_message", res.payload.message);
        setTemplateImages([]);
        setOldImages([]);
        setSnackbarMessage(res.payload.message);
        setSnackbarVariant("success");
        setShowSnackbar(true);
        navigate("/templates");
      } else {
        setErrors(res.payload.data);
        setSnackbarMessage(res.payload.message);
        setSnackbarVariant("danger");
        setShowSnackbar(true);
      }
    } catch (error) {
      console.error("Error updating template:", error);
      setSnackbarMessage("Failed to update template!");
      setSnackbarVariant("danger");
      setShowSnackbar(true);
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  if (!template)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <h2>Loading Template Details...</h2>
      </div>
    );

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      <Formik
        initialValues={{
          template_name: template?.template_name || "",
          description: template?.description || "",
        }}
        enableReinitialize
        validationSchema={updateTemplateSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          handleChange,
          handleSubmit,
          values,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit} encType="multipart/form-data">
            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel
                    htmlFor="template_name"
                    title="Template Name"
                  />
                  <Input
                    id="template_name"
                    name="template_name"
                    type="text"
                    placeholder="Enter Template Name"
                    onChange={handleChange}
                    value={values.template_name}
                    className={`shadow-none ${
                      errors?.template_name && touched.template_name
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.template_name && errors?.template_name && (
                    <ValidationFeedback title={errors?.template_name} />
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel htmlFor="description" title="Description" />
                  <Editor
                    value={values.description}
                    onChange={(content) =>
                      setFieldValue("description", content)
                    }
                  />
                  {touched.description && errors?.description && (
                    <ValidationFeedback title={errors?.description} />
                  )}
                </Col>
              </Row>
            </Card>


            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">Images</h4>

              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel
                    htmlFor="template_images"
                    title="Template Images"
                  />
                  <Input
                    id="template_images"
                    name="template_images"
                    type="file"
                    onChange={handleImageChange}
                    className={`shadow-none`}
                    multiple
                  />
                  {touched.template_images && errors?.template_images && (
                    <ValidationFeedback title={errors?.template_images} />
                  )}
                </Col>
              </Row>

              {templateImages.length > 0 && (
                <>
                  <hr />
                  <div className="mb-4 common-popup-heading">
                    <p className="mb-4">New Selected Images</p>
                    <Row>
                      {templateImages.map((image, index) => (
                        <Col md={4} key={index} className="mb-4">
                          <div className="image-preview text-center mb-4">
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`New Preview ${index}`}
                              className="img-fluid rounded border"
                              style={{ width: "200px", height: "auto" }}
                            />
                            <div className="d-flex align-items-center justify-content-center gap-3 common-popup-btn">
                              <button
                                type="button"
                                className="btn btn-danger btn-sm mt-2"
                                onClick={() => handleRemoveImage(index)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </>
              )}

              {oldImages.length > 0 && (
                <>
                  <hr />
                  <div className="mb-4 common-popup-heading">
                    <p className="mb-4">Uploaded Images</p>
                    <Row>
                      {oldImages.map((image) => (
                        <Col md={4} key={image.id} className="mb-4">
                          <div className="image-preview text-center mb-4">
                            <img
                              src={image.image_url}
                              alt={`Uploaded ${image.id}`}
                              className="img-fluid rounded border"
                              style={{ width: "200px", height: "auto" }}

                            />
                            <div className="d-flex align-items-center justify-content-center gap-3 common-popup-btn">
                              <button
                                type="button"
                                className="btn btn-danger btn-sm mt-2"
                                onClick={() => handleRemoveOldImage(image.id)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </>
              )}
            </Card>

            <div className="d-flex align-items-center justify-content-end gap-3">
              <CommonButton
                title={isSubmitting ? "Saving..." : "Save"}
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
              />
              <CommonButton
                title="Cancel"
                onClick={(e) => {
                  e.preventDefault();
                  handleModalClose();
                }}
                className="btn-muted"
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
